import React, { useRef, useEffect, useState } from "react";
import { Task } from "../../types/public-types";
import { BarTask } from "../../types/bar-task";
import styles from "./tooltip.module.css";

export type TooltipProps = {
  task: BarTask;
  type: string;
  arrowIndent: number;
  rtl: boolean;
  svgContainerHeight: number;
  svgContainerWidth: number;
  svgWidth: number;
  headerHeight: number;
  taskListWidth: number;
  scrollX: number;
  scrollY: number;
  rowHeight: number;
  fontSize: string;
  fontFamily: string;
  TooltipContent: React.FC<{
    task: Task;
    fontSize: string;
    fontFamily: string;
    type: string;
  }>;
  isDragging?: boolean;
};
export const Tooltip: React.FC<TooltipProps> = ({
  task,
  type,
  rowHeight,
  rtl,
  svgContainerHeight,
  svgContainerWidth,
  scrollX,
  scrollY,
  arrowIndent,
  fontSize,
  fontFamily,
  headerHeight,
  taskListWidth,
  TooltipContent,
  isDragging,
}) => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [relatedY, setRelatedY] = useState(0);
  const [relatedX, setRelatedX] = useState(0);
  useEffect(() => {
    if (tooltipRef.current) {
      const tooltipHeight = tooltipRef.current.offsetHeight * 1.1;
      const tooltipWidth = tooltipRef.current.offsetWidth * 1.1;

      let newRelatedY = task.index * rowHeight - scrollY + headerHeight;
      let newRelatedX: number;

      // During drag: pin to top-right corner of chart area so it never blocks the bar
      if (isDragging) {
        newRelatedX = taskListWidth + svgContainerWidth - tooltipWidth - 10;
        newRelatedY = headerHeight + 5;
        setRelatedY(newRelatedY);
        setRelatedX(newRelatedX);
        return;
      }

      if (rtl) {
        newRelatedX = task.x1 - arrowIndent * 1.5 - tooltipWidth - scrollX;
        if (newRelatedX < 0) {
          newRelatedX = task.x2 + arrowIndent * 1.5 - scrollX;
        }
        const tooltipLeftmostPoint = tooltipWidth + newRelatedX;
        if (tooltipLeftmostPoint > svgContainerWidth) {
          newRelatedX = svgContainerWidth - tooltipWidth;
          newRelatedY += rowHeight;
        }
      } else {
        newRelatedX =
          type == "planned"
            ? task.x2 + arrowIndent * 1.5 + taskListWidth - scrollX
            : task.actualx2 + arrowIndent * 1.5 + taskListWidth - scrollX;
        const tooltipLeftmostPoint = tooltipWidth + newRelatedX;
        const fullChartWidth = taskListWidth + svgContainerWidth;
        if (tooltipLeftmostPoint > fullChartWidth) {
          newRelatedX =
            type == "planned"
              ? task.x1 +
                taskListWidth -
                arrowIndent * 1.5 -
                scrollX -
                tooltipWidth
              : task.actualx1 +
                taskListWidth -
                arrowIndent * 1.5 -
                scrollX -
                tooltipWidth;
        }
        if (newRelatedX < taskListWidth) {
          newRelatedX = svgContainerWidth + taskListWidth - tooltipWidth;
          newRelatedY += rowHeight;
        }
      }

      const tooltipLowerPoint = tooltipHeight + newRelatedY - scrollY;
      if (tooltipLowerPoint > svgContainerHeight - scrollY) {
        newRelatedY = svgContainerHeight - tooltipHeight;
      }
      setRelatedY(newRelatedY);
      setRelatedX(newRelatedX);
    }
  }, [
    tooltipRef,
    task,
    arrowIndent,
    scrollX,
    scrollY,
    headerHeight,
    taskListWidth,
    rowHeight,
    svgContainerHeight,
    svgContainerWidth,
    rtl,
    isDragging,
  ]);

  return (
    <div
      ref={tooltipRef}
      className={
        relatedX
          ? styles.tooltipDetailsContainer
          : styles.tooltipDetailsContainerHidden
      }
      style={{ left: relatedX, top: relatedY }}
    >
      <TooltipContent
        task={task}
        fontSize={fontSize}
        fontFamily={fontFamily}
        type={type}
      />
    </div>
  );
};

export const StandardTooltipContent: React.FC<{
  task: Task;
  fontSize: string;
  fontFamily: string;
  type: string;
}> = ({ task, fontSize, fontFamily, type }) => {
  const style = {
    fontSize,
    fontFamily,
  };

  // Compute duration dynamically from the current dates (updated in real-time during drag)
  const computedPlannedDuration =
    task.start && task.end && task.end.getTime() - task.start.getTime() > 0
      ? Math.max(1, Math.round((task.end.getTime() - task.start.getTime()) / (1000 * 60 * 60 * 24)))
      : task.plannedDuration ?? 0;

  const computedActualDuration =
    task.actualStart && task.actualEnd && task.actualEnd.getTime() - task.actualStart.getTime() > 0
      ? Math.max(1, Math.round((task.actualEnd.getTime() - task.actualStart.getTime()) / (1000 * 60 * 60 * 24)))
      : task.actualDuration ?? 0;

  if (type == "planned")
    return (
      <div className={styles.tooltipDefaultContainer} style={style}>
        <b
          style={{ fontSize: fontSize + 6 }}
        >{`${task.name}: Planned dates: `}</b>
        <b>{`${
          task.start.getMonth() + 1
        }/${task.start.getDate()}/${task.start.getFullYear()} - ${
          task.end.getMonth() + 1
        }/${task.end.getDate()}/${task.end.getFullYear()}`}</b>
        {task.end.getTime() - task.start.getTime() !== 0 && (
          <p
            className={styles.tooltipDefaultContainerParagraph}
          >{`Duration: ${computedPlannedDuration} day(s)`}</p>
        )}

        <p className={styles.tooltipDefaultContainerParagraph}>
          {!!task.progress && `Progress: ${task.progress} %`}
        </p>
      </div>
    );
  else
    return (
      <div className={styles.tooltipDefaultContainer} style={style}>
        <b
          style={{ fontSize: fontSize + 6 }}
        >{`${task.name}: Actual dates: `}</b>
        <b>{`${
          task.actualStart.getMonth() + 1
        }/${task.actualStart.getDate()}/${task.actualStart.getFullYear()} - ${
          task.actualEnd.getMonth() + 1
        }/${task.actualEnd.getDate()}/${task.actualEnd.getFullYear()}`}</b>
        {task.actualEnd.getTime() - task.actualStart.getTime() !== 0 && (
          <p
            className={styles.tooltipDefaultContainerParagraph}
          >{`Duration: ${computedActualDuration} day(s)`}</p>
        )}

        <p className={styles.tooltipDefaultContainerParagraph}>
          {!!task.progress && `Progress: ${task.progress} %`}
        </p>
      </div>
    );
};
